import { useCallback, useEffect, useMemo, useState } from "react";
import { useFetch } from "../../../../utils/hooks/useFetch";
import { getResourcesListServiceConfig, getBulkStatusUpdateServiceConfig, getBulkDeleteUsersServiceConfig } from "../../../../utils/services/account";
import moment from "moment";
import { getQueryParams } from "../../../../utils/helpers";
import { translate } from "../../../../utils/translations/translate";
import { Link } from "react-router-dom";
import { DATE_TIME_FORMAT, DEFAULT_PAGE_SIZE, TOAST_TIMER } from "../../../../utils/constants";
import { useSelector } from "react-redux";
export const useAccountList = () => {
    const [list, setList] = useState([]);
    const {userInfo} = useSelector((store)=>store.user.authInfo) || {};
    const {ResourceID:currentUserId} = userInfo || {};
    const [showDeleteModal,setShowDeleteModal] = useState(false);
    const columnDefs = useMemo(() => {
        return [
            {
                headerName: translate('columnDefs.user.resourceId'),
                field: "resourceId",
                sortable: true,
                filter: false,
                width: 120,
                wrapHeaderText: true
            },
            {
                headerName: translate('columnDefs.user.resourceName'),
                field: "resourceName",
                sortable: true,
                filter: false,
                cellRenderer: (params) => {
                    const { data, value } = params;
                    const { resourceId } = data;
                    return <Link to={`/u/${resourceId}`}>{value}</Link>
                },
            },
            {
                headerName: translate('columnDefs.user.resourceType'),
                field: "resourceType",
                sortable: true,
                filter: false,
                width: 120,
            },
            { headerName: translate('labels.emailId'), field: "resourceEmail", sortable: true, filter: false },
            {
                headerName: translate('labels.phoneNumber'),
                field: "resourcePhoneNumber",
                sortable: true,
                filter: false,
                width: 150
            },
            {
                headerName: translate('labels.status'),
                field: "resourceStatus",
                sortable: true,
                filter: false,
                width: 100
            },
            {
                headerName: translate('labels.lastLogin'),
                field: "lastLogin",
                sortable: true,
                filter: false,
                width: 120
            },
            {
                headerName: translate('columnDefs.user.resouceCreatedOn'),
                field: "resourceCreatedOn",
                sortable: true,
                filter: false,
                wrapHeaderText: true,
                autoHeaderHeight: true,
                width: 120
            },
        ];
    }, [])
    const { isLoading, isError, performAction,hideServiceError } = useFetch();
    const { isLoading: isPageBusy, performAction: performBulkAction } = useFetch();
    const [selectedList, setSelectedList] = useState([]);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pageNumber, setPageNumber] = useState(1);
    const [resourceKey, setResourceKey] = useState('');
    const [resourceType, setResourceType] = useState('');
    const [statusKey, setStatusKey] = useState('Active');
    const [totalRecords, setTotalRecords] = useState(0);
    const [toastInfo, setToastInfo] = useState(null);
    const handlePaginationChange = useCallback(({ page, pageSize }) => {
        setPageNumber(page);
        setPageSize(pageSize);
    }, [])
    const updateSelectedList = useCallback((nodeData) => {
        const { id, selected } = nodeData;
        setSelectedList((prev) => {
            const tmpList = prev.slice();
            const existingRowIdx = tmpList.findIndex(item => item.id === id);
            if (existingRowIdx > -1 && !selected) {
                tmpList.splice(existingRowIdx, 1);
            }
            else {
                tmpList.push(nodeData);
            }
            return tmpList;
        })
    }, [])
    const fetchUsersList = useCallback(async (queryParams) => {
        try {
            const { url, options } = getResourcesListServiceConfig(queryParams);
            const response = await performAction(url, options);
            if (response && typeof response === "object" && Array.isArray(response.Result)) {
                const formattedResults = response.Result.map((resource) => {
                    const { ResourceID, ResourceName, AccountType, Email, CreatedOn, Phone, Status, LastLoginAgo, } = resource;
                    return {
                        "resourceId": ResourceID,
                        "resourceName": ResourceName,
                        "resourceType": AccountType,
                        "resourceEmail": Email,
                        "resourcePhoneNumber": Phone || "NA",
                        "lastLogin": LastLoginAgo || "NA",
                        "resourceStatus": Status,
                        "resourceCreatedOn": moment(CreatedOn).format(DATE_TIME_FORMAT.DISPLAY),
                        "isEditable":currentUserId === ResourceID ? false:true
                    }
                })
                const sortedResults = formattedResults;
                setList(sortedResults);
                setTotalRecords(response.Result[0]?.TotalRecords || 0);
            }
        }
        catch (e) {
            console.error("Error fetching resources");
        }
    }, [performAction,currentUserId])
    const bulkUpdateStatus = useCallback(async (selectedIds, status) => {
        try {
            if(selectedIds.length === 0){
                setToastInfo({
                    message: `Selected Resource(s) are already ${status}`,
                    severity: "info"
                })
                return;
            }
            const { url, options } = getBulkStatusUpdateServiceConfig(selectedIds, status);
            const response = await performBulkAction(url, options);
            if (response.StatusCode === 200) {
                setToastInfo({
                    message: translate("labels.statusUpdatedMessage"),
                    severity: "success"
                })
                setList(prevList => {
                    const tmpList = JSON.parse(JSON.stringify([...prevList]));
                    selectedIds.forEach(id => {
                        const refRowIdx = tmpList.findIndex(rsrc => rsrc.resourceId === id);
                        if (refRowIdx > -1) {
                            tmpList[refRowIdx].resourceStatus = status;
                        }
                    })
                    return tmpList;
                })
            }
            else {
                throw new Error("Error updating users");
            }
        }
        catch (e) {
            console.error('Error updating the users')
            setToastInfo({
                message: e.message,
                severity: "error"
            })
        }
        finally {
            setTimeout(() => {
                setToastInfo(null);
            }, TOAST_TIMER)
        }
    }, [performBulkAction])
    const bulkDeleteUsers = async () => {
        try {
            setShowDeleteModal(false);
            const selectedIds = selectedList.map(list=>list.id)
            const { url, options } = getBulkDeleteUsersServiceConfig(selectedIds);
            const response = await performBulkAction(url, options);
            if (response.StatusCode === 200) {
                setToastInfo({
                    message: translate('labels.deleteSuccessfulMsg'),
                    severity: "info"
                })
                const additionalParams = {
                    PageNo: pageNumber,
                    PageSize: pageSize,
                    Status: statusKey,
                    Search: resourceKey,
                    AccountType: resourceType
                }
                const queryParams = getQueryParams(additionalParams);
                await fetchUsersList(queryParams);
            }
            else {
                throw new Error("Error deleting users");
            }
        }
        catch (e) {
            console.error('Error deleting  the users')
            setToastInfo({
                message: e.message,
                severity: "error"
            })
        }
        finally {
            setTimeout(() => {
                setToastInfo(null);
            }, TOAST_TIMER)
        }
    }
    const handleToastClose = useCallback(()=>{
        setToastInfo(null);
    },[])
    useEffect(() => {
        setPageNumber(1);
    }, [resourceKey, resourceType, statusKey])
    useEffect(() => {
        const additionalParams = {
            PageNo: pageNumber,
            PageSize: pageSize,
            Status: statusKey,
            Search: resourceKey,
            AccountType: resourceType
        }
        const queryParams = getQueryParams(additionalParams);
        let delay = 0;
        if (additionalParams.Search) {
            delay = 1500;
        }
        const fetchTimer = setTimeout(() => {
            fetchUsersList(queryParams);
        }, delay)
        return () => {
            clearTimeout(fetchTimer);
        }
    }, [fetchUsersList, statusKey, resourceKey, pageNumber, pageSize, resourceType])
    return {
        isLoading,
        isError,
        columnDefs,
        list,
        pageNumber,
        pageSize,
        resourceKey,
        totalRecords,
        statusKey,
        resourceType,
        setResourceKey,
        setResourceType,
        setStatusKey,
        handlePaginationChange,
        updateSelectedList,
        selectedList,
        bulkUpdateStatus,
        isPageBusy,
        toastInfo,
        showDeleteModal,
        hideServiceError,
        setShowDeleteModal,
        handleToastClose,
        bulkDeleteUsers
    }
}